import { useModals } from '@/composables/useModal/useModal'
import constants from '@/exports/constants'
import helpers from '@/exports/helper'
import httpClient from '@/httpClient'
import i18n from '@/i18n'
import {
  CallingCallDirection,
  CallingCallType,
  CallingFilter,
  CreateRoomError
} from '@/models/enums'
import type {
  ICallingDownload,
  ICallingItem,
  ICompleteRoomRequest,
  IGetCallsRequest,
  IJoinRoomRequest,
  ILogActionRequest,
  INewSessionParam,
  ISingleCallReportParam,
  IUpdateCallRequest,
  IUpdateVideoCallStatusRequest,
  IUpdateVoicemailStatusRequest,
  IVideoCall,
  WorkerRequest
} from '@/models/interfaces'
import type { ICreateRoomRequest, IJoinRoomResult } from '@/models/modelsV3'
import type { ICallingState } from '@/models/stores/calling'
import WorkerService from '@/services/webWorkerService'
import { watchIgnorable } from '@vueuse/core'
import { DateTime } from 'luxon'
import { defineStore, storeToRefs } from 'pinia'
import { isSupported } from 'twilio-video'
import { computed, reactive, toRefs, watch } from 'vue'
import ErrorHelper from '../exports/error'
import fileDownloaderWorker from '../workers/fileDownloader?worker&url'
import { useAccountSettingsStore } from './AccountSettingsStore'
import { useCommonStore } from './CommonStore'
import { CreateSessionError } from '@/pages/calling/config/calling.enums'

const baseUrl = '/mobile/api'

const callingControllerBaseUrl = `${baseUrl}/calling`
const sessionControllerBaseUrl = `${baseUrl}/session`

const videoCallingControllerBaseUrl = `${baseUrl}/videocalling`

const singleCallReportControllerBaseUrl = `${baseUrl}/singlecallreport`
const singleVideoCallReportControllerBaseUrl = `${baseUrl}/singlevideocallreport`
const singleVoicemailReportControllerBaseUrl = `${baseUrl}/singlevoicemailreport`

const paths: string[] = ['videoInputId', 'audioInputId', 'audioOutputId']
const initialState = (): ICallingState => ({
  showVideoCall: false,
  videoCallThreadId: null,
  callThreadId: null,
  startVideoCall: false,
  roomResult: null,
  activeCall: null,
  canCallStatus: new Map([
    [constants.CAN_CALL_STATUS.videoAllowed, false],
    [constants.CAN_CALL_STATUS.phoneAllowed, false],
    [constants.CAN_CALL_STATUS.coParentPhoneDisabled, false],
    [constants.CAN_CALL_STATUS.coParentVideoDisabled, false],
    [constants.CAN_CALL_STATUS.notPremium, false],
    [constants.CAN_CALL_STATUS.phoneCallingDisabled, false],
    [constants.CAN_CALL_STATUS.videoCallingDisabled, false],
    [constants.CAN_CALL_STATUS.coParentTierFree, false],
    [constants.CAN_CALL_STATUS.noMinutes, false]
  ]),
  callingItems: [
    {
      callType: CallingCallType.Phone,
      direction: CallingCallDirection.Outgoing,
      itemID: 0,
      caseID: 0,
      userID: 0,
      toUserID: 0,
      createdWhen: DateTime.now(),
      startTime: DateTime.now(),
      endTime: DateTime.now(),
      viewedWhen: DateTime.now(),
      status: '',
      isMissedCall: false,
      isUnseen: false,
      skeletonLoading: true
    },
    {
      callType: CallingCallType.Phone,
      direction: CallingCallDirection.Outgoing,
      itemID: 0,
      caseID: 0,
      userID: 0,
      toUserID: 0,
      createdWhen: DateTime.now(),
      startTime: DateTime.now(),
      endTime: DateTime.now(),
      viewedWhen: DateTime.now(),
      status: '',
      isMissedCall: false,
      isUnseen: false,
      skeletonLoading: true
    },
    {
      callType: CallingCallType.Phone,
      direction: CallingCallDirection.Outgoing,
      itemID: 0,
      caseID: 0,
      userID: 0,
      toUserID: 0,
      createdWhen: DateTime.now(),
      startTime: DateTime.now(),
      endTime: DateTime.now(),
      viewedWhen: DateTime.now(),
      status: '',
      isMissedCall: false,
      isUnseen: false,
      skeletonLoading: true
    }
  ],
  callingPageSize: 25,
  callingPageNumber: 1,
  loadMoreCallingItems: false,
  selectedCallingItem: null,
  selectedCallingFilter: CallingFilter.All,
  callingSearchTerm: '',
  coparentCallingSettings: null,
  showCallingSetup: false,
  introVideoDiagnostics: false,
  showAudioCall: false,
  proxyPhoneNumber: 0,
  downloads: null,
  videoInputId: '',
  audioInputId: '',
  audioOutputId: '',
  unseenVoicemailCount: 0,
  videoCallDeclined: false,
  showVideoCallConfirmation: false,
  showVideoCallError: false,
  phoneSessionError: null
})

export const useCallingStore = defineStore(
  'calling',
  () => {
    //Options syntax, Composition syntax
    //state, ref
    const state = reactive<ICallingState>(initialState())

    //getters, computed

    // resets whatever needs to be refetched when entering calling
    // not resetting anything that will be used during a video call
    function resetMainCallingFeature() {
      state.callingItems = [
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        },
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        },
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        }
      ]

      state.callingPageSize = 25
      state.callingPageNumber = 1
      state.loadMoreCallingItems = false
      state.selectedCallingItem = null
      state.selectedCallingFilter = CallingFilter.All
      state.callingSearchTerm = ''
      state.showCallingSetup = false
    }

    const newCallIsAvailable = computed(() => {
      return state.activeCall
    })

    const isBrowserSupportedForTwilio = computed(() => {
      return isSupported
    })

    const isActiveCallIncoming = computed(() => {
      const { fullUserInfo } = storeToRefs(useCommonStore())
      return state.activeCall?.toUserID == fullUserInfo.value.userId
    })

    const initialLoad = computed(
      () => !!state.callingItems.find((ci) => ci.skeletonLoading)
    )

    const showMobileHeaderCog = computed(
      () =>
        !state.showAudioCall &&
        !state.selectedCallingItem &&
        !state.showVideoCallError &&
        !state.showCallingSetup
    )

    //actions, functions

    function reset() {
      Object.assign(
        state,
        helpers.omit(initialState(), paths as (keyof ICallingState)[])
      )
    }

    function setShowVideoCall(val: boolean) {
      state.showVideoCall = val
    }

    function setShowVideoCallConfirmation(val: boolean) {
      state.showVideoCallConfirmation = val
    }

    function setShowVideoCallError(val: boolean) {
      state.showVideoCallError = val
    }

    function setStartVideoCall(val: boolean) {
      state.startVideoCall = val
    }

    function setCallingItems(items: ICallingItem[]) {
      state.callingItems = items
    }

    function resetCallingItems() {
      setCallingItems([
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        },
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        },
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        }
      ])
    }

    function setSelectedCallingItem(item: ICallingItem | null) {
      state.selectedCallingItem = item
    }

    function setLoadMoreCallingItems(loadMore: boolean) {
      state.loadMoreCallingItems = loadMore
    }

    function setSelectedCallingFilter(filter: CallingFilter) {
      state.selectedCallingFilter = filter
    }

    function setPageNumber(page: number) {
      state.callingPageNumber = page
    }

    function setActiveCall(payload: IVideoCall | null) {
      state.activeCall = payload
    }

    function setIntroVideoDiagnostics(show: boolean) {
      state.introVideoDiagnostics = show
    }

    function setShowCallingSetup(show: boolean) {
      state.showCallingSetup = show
    }

    function setShowAudioCall(show: boolean) {
      state.showAudioCall = show
    }

    async function fetchCallingItems() {
      //page number is zero-based
      const items = (
        await fetchCalls({
          filter: state.selectedCallingFilter,
          pageNumber: state.callingPageNumber - 1,
          searchTerm: state.callingSearchTerm,
          pageSize: state.callingPageSize
        })
      )?.data.calls as ICallingItem[]

      //first page? overwrite, otherwise, append
      setCallingItems(
        state.callingPageNumber == 1 ? items : [...state.callingItems, ...items]
      )
      setLoadMoreCallingItems(items.length == state.callingPageSize)
    }

    async function fetchMoreCallingItems() {
      await fetchCallingItemsByPage(++state.callingPageNumber)
    }

    async function refreshNewCallingItems() {
      const items =
        ((
          await fetchCalls({
            filter: state.selectedCallingFilter,
            pageNumber: 0,
            searchTerm: state.callingSearchTerm,
            pageSize: state.callingPageSize
          })
        )?.data.calls as ICallingItem[]) ?? []

      const existingIds = new Set(
        state.callingItems.map((ci) => `${ci.callType}:${ci.itemID}`)
      )
      const newItems = items.filter(
        (item) => !existingIds.has(`${item.callType}:${item.itemID}`)
      )
      if (newItems.length > 0) {
        setCallingItems([...newItems, ...state.callingItems])
      }
    }

    async function fetchCallingItemsByPage(page: number) {
      setPageNumber(page)

      await fetchCallingItems()
    }

    const { ignoreUpdates: ignoreFilterAndSearchUpdates } = watchIgnorable(
      [() => state.callingSearchTerm, () => state.selectedCallingFilter],
      async () => {
        setSelectedCallingItem(null)
        resetCallingItems()
        await fetchCallingItemsByPage(1)
      }
    )

    watch(
      () => state.callingItems,
      async (val) => {
        if (Array.isArray(val) && val.length > 0) {
          const { setShowingNoCount } = useCommonStore()
          setShowingNoCount(false)
        } else if (
          state.selectedCallingFilter == CallingFilter.All &&
          !state.callingSearchTerm.length
        ) {
          const { setShowingNoCount } = useCommonStore()
          setShowingNoCount(true)
        }
      }
    )

    // The consumer GET marks the voicemail viewed on the server. Only its
    // shell performs this local bookkeeping; observer reads never get here.
    function markVoicemailViewed(
      item: ICallingItem,
      recipientUserId = item.toUserID
    ): void {
      const commonStore = useCommonStore()
      const { fullUserInfo } = storeToRefs(commonStore)
      const { setUnseenVoicemailBadgecount } = commonStore

      if (
        item.callType !== CallingCallType.Voicemail ||
        recipientUserId !== fullUserInfo.value.userId
      )
        return
      const listed = state.callingItems.find(
        (candidate) =>
          candidate.callType === CallingCallType.Voicemail &&
          candidate.itemID === item.itemID
      )
      item.isUnseen = false
      if (!listed) {
        // Messages/Records selections omit view state. Ask the server rather
        // than guessing whether this GET changed the unseen count.
        void fetchUnseenVoicemailCount()
        return
      }
      const wasUnseen = listed.isUnseen
      listed.isUnseen = false
      if (wasUnseen && state.unseenVoicemailCount > 0) {
        state.unseenVoicemailCount--
        setUnseenVoicemailBadgecount(state.unseenVoicemailCount)
      }
    }

    function setCallingSearchTerm(searchTerm: string) {
      state.callingSearchTerm = searchTerm
    }

    // API Calls
    //#region CallingController
    async function fetchCalls(payload: IGetCallsRequest) {
      const endpoint = `${callingControllerBaseUrl}/getcalls?Filter=${payload.filter}&PageNumber=${payload.pageNumber}&SearchTerm=${encodeURIComponent(payload.searchTerm)}&PageSize=${payload.pageSize}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 3 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCalls')
      }
    }

    async function fetchCoparentCallingSettings() {
      const endpoint = `${callingControllerBaseUrl}/getcoparentcallingsettings`

      try {
        const results = await httpClient.get(endpoint, {
          headers: { 'api-version': 3 }
        })
        state.coparentCallingSettings = results.data
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCoparentCallingSettings')
      }
    }

    //#endregion CallingController

    //#region PhoneCalling/SessionController
    async function sessionPost(nsp: INewSessionParam) {
      const endpoint = sessionControllerBaseUrl
      state.phoneSessionError = null

      try {
        const result = (
          await httpClient.post(endpoint, nsp, {
            headers: { 'api-version': '3.0' }
          })
        ).data

        if (result) {
          if (result.success) {
            state.proxyPhoneNumber = result.value
            return state.proxyPhoneNumber
          } else if (
            result.errorCode === CreateSessionError.CountryCodeNotSupported
          ) {
            state.phoneSessionError = CreateSessionError.CountryCodeNotSupported
          } else if (result.errorCode === CreateSessionError.CallInProgress) {
            ErrorHelper.handleError(
              new Error(result.errorMessage),
              'sessionPost',
              true,
              result.errorMessage
            )
          } else {
            throw new Error(result.errorMessage)
          }
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'sessionPost')
      }
    }
    //#endregion PhoneCalling/SessionController

    //#region VideoCalling/VideoCallingController
    async function getTestAccessToken() {
      const endpoint = `${videoCallingControllerBaseUrl}/GetTestAccessToken`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 3 }
        })
        if (result) {
          return result
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'getActiveVideoCall')
      }
    }

    async function createRoom(request: ICreateRoomRequest) {
      const endpoint = `${videoCallingControllerBaseUrl}/createRoom`

      try {
        const result = await httpClient.post(endpoint, request, {
          headers: { 'api-version': 3 }
        })
        state.roomResult = result.data
        if (!state.roomResult?.success) {
          if (errorIsUserIssue(state.roomResult?.errorCode ?? 0)) {
            setShowVideoCallError(true)
          } else {
            throw new Error(state.roomResult?.errorMessage)
          }
        } else {
          return true
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'createRoom')
        return false
      }
    }

    function errorIsUserIssue(errorCode: number) {
      // Not all of these are actually sent by the api when called createRoom, but adding it in case the api is updated
      setShowVideoCall(false)
      switch (errorCode) {
        case CreateRoomError.CoparentDisabledVideoCalls:
          setCanCallStatus(
            constants.CAN_CALL_STATUS.coParentVideoDisabled,
            true
          )
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        case CreateRoomError.FeatureDisabled:
          setCanCallStatus(constants.CAN_CALL_STATUS.videoCallingDisabled, true)
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        case CreateRoomError.NoMinutes:
          setCanCallStatus(constants.CAN_CALL_STATUS.noMinutes, true)
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        case CreateRoomError.RecipientHasInvalidTier:
          setCanCallStatus(constants.CAN_CALL_STATUS.coParentTierFree, true)
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        case CreateRoomError.UserNotPremium:
          setCanCallStatus(constants.CAN_CALL_STATUS.notPremium, true)
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        default:
          return false
      }
    }

    async function getActiveVideoCall() {
      const endpoint = `${videoCallingControllerBaseUrl}/GetActiveVideoCall`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          setActiveCall(result.data.value)
        }
        return true
      } catch (e) {
        ErrorHelper.handleError(e, 'getActiveVideoCall')
        return false
      }
    }

    async function joinRoom(
      request: IJoinRoomRequest
    ): Promise<IJoinRoomResult | undefined> {
      const endpoint = `${videoCallingControllerBaseUrl}/joinRoom?joinVideoCallItemID=${request.joinVideoCallItemID}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 3 }
        })
        if (result) {
          state.roomResult = result.data
        }
        return result.data as IJoinRoomResult
      } catch (e) {
        ErrorHelper.handleError(e, 'joinRoom')
      }
    }

    async function completeRoom(request: ICompleteRoomRequest) {
      const endpoint = `${videoCallingControllerBaseUrl}/completeRoom?type=${request.type}&itemID=${request.itemID}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 3 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'completeRoom')
      }
    }

    async function updateCall(request: IUpdateCallRequest) {
      const endpoint = `${videoCallingControllerBaseUrl}/updateCall?itemID=${request.itemID}&callType=${request.callType}&newStatus=${request.newStatus}&action=${request.action}&actionData=${request.actionData}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 3 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'updateCall')
      }
    }

    async function updateVideoCallStatus(
      request: IUpdateVideoCallStatusRequest
    ) {
      const endpoint = `${videoCallingControllerBaseUrl}/updateVideoCallStatus?videoCallId=${request.videoCallId}&newStatus=${request.newStatus}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'updateVideoCallStatus')
      }
    }

    async function updateVoicemailStatus(
      request: IUpdateVoicemailStatusRequest
    ) {
      const endpoint = `${videoCallingControllerBaseUrl}/updateVoicemailStatus?voicemailID=${request.voicemailId}&newStatus=${request.newStatus}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'updateVoicemailStatus')
      }
    }

    async function logAction(payload: ILogActionRequest) {
      const endpoint = `${videoCallingControllerBaseUrl}/LogAction`

      try {
        return await httpClient.post(endpoint, payload, {
          headers: { 'api-version': 2 }
        })
      } catch (e) {
        ErrorHelper.handleError(e, 'completeCallingSetup')
      }
    }

    async function getActiveVideoCallByItemID(itemID: number | undefined) {
      if (itemID == undefined) return
      const endpoint = `${videoCallingControllerBaseUrl}/GetVideoCall?ItemID=${itemID}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          setActiveCall(result.data.value)
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'GetVideoCallRecording')
        return false
      }
    }

    //#endregion VideoCalling/VideoCallingController
    //#region Video Voicemails
    async function fetchUnseenVoicemailCount() {
      const endpoint = `${videoCallingControllerBaseUrl}/GetUnseenVoicemailCount`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          state.unseenVoicemailCount = result.data.unseenVoicemailCount
          const { setUnseenVoicemailBadgecount } = useCommonStore()
          setUnseenVoicemailBadgecount(state.unseenVoicemailCount)
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'GetVideoVoicemailTranscript')
        return false
      }
    }
    //#endregion

    //#region Precheck
    async function bothParentsPassPrecheck() {
      const accountSettingsStore = useAccountSettingsStore()
      const { subscriptionT0, subscriptionT2, callingBalance, phoneInfo } =
        storeToRefs(accountSettingsStore)
      const { fetchPhoneInfo, fetchCallingBalance } = accountSettingsStore

      const { featureFlags } = storeToRefs(useCommonStore())

      resetCanCallStatus()
      setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, true)
      setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, true)

      // fetch phone info
      await fetchPhoneInfo()
      // fetch calling balance
      await fetchCallingBalance()
      // fetch coparent settings
      await fetchCoparentCallingSettings()

      if (subscriptionT0.value && !featureFlags.value?.web_freeplan) {
        setCanCallStatus(constants.CAN_CALL_STATUS.notPremium, true) // subscription tier
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, false)
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }

      if (
        callingBalance.value != null &&
        callingBalance.value <= 0 &&
        !subscriptionT2.value
      ) {
        setCanCallStatus(constants.CAN_CALL_STATUS.noMinutes, true) // not enough minutes
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, false)
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }

      if (
        !phoneInfo.value.allowCallsFromMatchedUser ||
        !phoneInfo.value.isUserMobilePhoneVerified
      ) {
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneCallingDisabled, true) // disabled phone calling
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, false) // if checking for video call, this status does not matter
      }

      if (!phoneInfo.value.allowsVideoCalls) {
        setCanCallStatus(constants.CAN_CALL_STATUS.videoCallingDisabled, true) // disabled video calling
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }

      if (!state.coparentCallingSettings?.allowsPhoneCalls) {
        setCanCallStatus(constants.CAN_CALL_STATUS.coParentPhoneDisabled, true) // coparent issue
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, false)
      }

      if (!state.coparentCallingSettings?.allowsVideoCalls) {
        setCanCallStatus(constants.CAN_CALL_STATUS.coParentVideoDisabled, true) // coparent issue
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }

      if (
        !state.coparentCallingSettings
          ?.isSubscriptionCapableOfReceivingVideoCall &&
        !featureFlags.value?.web_freeplan
      ) {
        setCanCallStatus(constants.CAN_CALL_STATUS.coParentTierFree, true) // coparent issue
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }
    }

    async function tryShowVideoCall() {
      if (state.showVideoCall || state.showVideoCallError) return
      await bothParentsPassPrecheck()
      const canMakeVideoCall =
        state.canCallStatus.get(constants.CAN_CALL_STATUS.videoAllowed) ?? false

      if (canMakeVideoCall) {
        setShowVideoCall(true)
      } else {
        setShowVideoCallError(true)
      }
    }
    //#endregion

    //#region SingleCallReportController
    async function singleCallReportPost(p: ISingleCallReportParam) {
      try {
        const result = (
          await httpClient.post(singleCallReportControllerBaseUrl, p, {
            headers: { 'api-version': 2 }
          })
        ).data

        return result.success
      } catch (e) {
        ErrorHelper.handleError(e, 'singleCallReportPost')
        return false
      }
    }
    //#endregion SingleCallReportController
    //#region SingleVideoCallReportController
    async function singleVideoCallReportPost(p: ISingleCallReportParam) {
      try {
        const result = (
          await httpClient.post(singleVideoCallReportControllerBaseUrl, p, {
            headers: { 'api-version': 2 }
          })
        ).data

        return result.success
      } catch (e) {
        ErrorHelper.handleError(e, 'singleVideoCallReportPost')
        return false
      }
    }
    //#endregion SingleVideoCallReportController
    //#region SingleVoicemailReportController
    async function singleVoicemailReportPost(p: ISingleCallReportParam) {
      try {
        const result = (
          await httpClient.post(singleVoicemailReportControllerBaseUrl, p, {
            headers: { 'api-version': 2 }
          })
        ).data

        return result.success
      } catch (e) {
        ErrorHelper.handleError(e, 'singleVoicemailReportPost')
        return false
      }
    }
    //#endregion SingleVoicemailReportController

    function startDownload(
      url: string,
      fileName: string,
      callingItem: ICallingItem
    ) {
      const id = Date.now().toString() // Generate a unique ID for the download

      WorkerService.createWorker(id, fileDownloaderWorker)
      const options: WorkerRequest['options'] = {}
      const request: WorkerRequest = { url, options, id }

      WorkerService.postRequest(id, request, (response) => {
        if (response.success) {
          if (response.data.status === 'progress') {
            if (response.data.id === id) {
              const download = getDownloadFromList(id)
              if (download) {
                download.progress = response.data.progress
              }
            }
          } else if (response.data.status === 'completed') {
            const download = getDownloadFromList(id)
            if (download) {
              handleDownloadedFile(response.data.data, download.fileName)
              download.status = constants.vaultDownloadStatus.completed
            }
            WorkerService.terminateWorker(id)
          } else if (response.data.status === 'error') {
            const download = getDownloadFromList(id)
            if (download) {
              download.status = constants.vaultDownloadStatus.failed
            }
            WorkerService.terminateWorker(id)
          }
        } else {
          const download = getDownloadFromList(id)
          if (download) {
            download.status = constants.vaultDownloadStatus.failed
          }
        }
      })

      state.downloads = [
        ...(state.downloads ?? []),
        ...[
          {
            id,
            url,
            progress: 0,
            fileName,
            status: constants.vaultDownloadStatus.downloading,
            callingItem: callingItem
          }
        ]
      ]
    }

    function reDownload(existingFile: ICallingDownload) {
      //const workerUrl = new URL('../workers/fileDownloader.ts', import.meta.url)
      WorkerService.createWorker(existingFile.id, fileDownloaderWorker)
      const options: WorkerRequest['options'] = {}
      const request: WorkerRequest = {
        url: existingFile.url,
        options,
        id: existingFile.id
      }

      WorkerService.postRequest(existingFile.id, request, (response) => {
        if (response.success) {
          if (response.data.status === 'progress') {
            if (response.data.id === existingFile.id) {
              const download = getDownloadFromList(existingFile.id)
              if (download) {
                download.progress = response.data.progress
              }
            }
          } else if (response.data.status === 'completed') {
            const download = getDownloadFromList(existingFile.id)
            if (download) {
              handleDownloadedFile(response.data.data, download.fileName)
              download.status = constants.vaultDownloadStatus.completed
            }
            WorkerService.terminateWorker(existingFile.id)
          } else if (response.data.status === 'error') {
            const download = getDownloadFromList(existingFile.id)
            if (download) {
              download.status = constants.vaultDownloadStatus.failed
            }
            WorkerService.terminateWorker(existingFile.id)
          }
        } else {
          const download = getDownloadFromList(existingFile.id)
          if (download) {
            download.status = constants.vaultDownloadStatus.failed
          }
        }
      })
    }

    function handleDownloadedFile(blob: Blob, fileName: string) {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    }

    function stopDownload(id: string) {
      const download = getDownloadFromList(id)
      if (download) {
        download.status = constants.vaultDownloadStatus.stopped
        WorkerService.terminateWorker(id)
      }
    }

    function removeDownloadFromList(id: string) {
      if (!state.downloads) {
        return
      }

      const downloadIndex = state.downloads.findIndex((d) => d.id === id)
      if (downloadIndex >= 0) {
        WorkerService.terminateWorker(id)
        state.downloads.splice(downloadIndex, 1)
      }
    }

    function getDownloadFromList(id: string) {
      return state.downloads?.find((d) => d.id === id)
    }

    function removeAllDownloads() {
      state.downloads?.forEach((d) => {
        if (d.status == constants.vaultDownloadStatus.downloading) {
          WorkerService.terminateWorker(d.id)
        }
      })
      state.downloads = null
    }

    const getInProgressDownloads = computed(() => {
      return state.downloads?.filter(
        (d) => d.status == constants.vaultDownloadStatus.downloading
      )
    })

    // download modals
    function cancelAllDownloads() {
      const downloadString = `<span class="place-center">${i18n.global.t('downloads.inProgressCancelModal.content')}</span>`

      if (getInProgressDownloads.value?.length) {
        const { createSlot, closeModal, generateModal, HTMLtoComponent } =
          useModals()

        const el = generateModal({
          default: {
            headerText: i18n.global.t('downloads.inProgressCancelModal.header'),
            footerButtonLabel: i18n.global.t(
              'downloads.inProgressCancelModal.confirmButton'
            )
          },
          slot: {
            content: createSlot('content', HTMLtoComponent(downloadString))
              .content
          },
          config: {
            showHeader: true,
            showBody: true,
            showFooter: true,
            addContentPadding: true,
            closeOnConfirm: true,
            showCloseButton: true,
            secondaryCTALabel: i18n.global.t(
              'downloads.inProgressCancelModal.cancelButton'
            )
          },
          callback: {
            confirmFn: () => {
              removeAllDownloads()
            },
            secondaryFn: () => {
              closeModal(el)
            }
          }
        })
      } else {
        removeAllDownloads()
      }
    }

    function setMediatId(
      type: 'videoinput' | 'audioinput' | 'audiooutput',
      id: string
    ) {
      switch (type) {
        case 'videoinput':
          state.videoInputId = id
          break
        case 'audioinput':
          state.audioInputId = id
          break
        case 'audiooutput':
          state.audioOutputId = id
          break
      }
    }

    function setVideoCallThreadId(val: number | null) {
      state.videoCallThreadId = val
    }

    function setCanCallStatus(statusKey: string, statusValue: boolean) {
      state.canCallStatus.set(statusKey, statusValue)
    }

    function resetCanCallStatus() {
      for (const key of state.canCallStatus.keys()) {
        state.canCallStatus.set(key, false)
      }
    }
    function setCallThreadId(val: number | null) {
      state.callThreadId = val
    }

    function setProxyPhoneNumber(val: number) {
      state.proxyPhoneNumber = val
    }

    function setCoparentDecliningCall(videoCallId?: number) {
      if (state.activeCall?.itemID == videoCallId) {
        state.videoCallDeclined = true
      } else {
        state.videoCallDeclined = false
      }
    }

    function setPhoneSessionError(error: CreateSessionError | null) {
      state.phoneSessionError = error
    }

    return {
      fetchCalls,
      fetchCoparentCallingSettings,
      sessionPost,
      createRoom,
      joinRoom,
      completeRoom,
      updateCall,
      setStartVideoCall,
      isBrowserSupportedForTwilio,
      getActiveVideoCall,
      newCallIsAvailable,
      setCallingItems,
      resetCallingItems,
      setSelectedCallingItem,
      setLoadMoreCallingItems,
      setSelectedCallingFilter,
      fetchCallingItems,
      setPageNumber,
      fetchMoreCallingItems,
      refreshNewCallingItems,
      fetchCallingItemsByPage,
      markVoicemailViewed,
      isActiveCallIncoming,
      getActiveVideoCallByItemID,
      setShowVideoCall,
      setShowVideoCallConfirmation,
      logAction,
      updateVideoCallStatus,
      updateVoicemailStatus,
      setActiveCall,
      setShowCallingSetup,
      bothParentsPassPrecheck,
      setShowAudioCall,
      singleCallReportPost,
      singleVideoCallReportPost,
      singleVoicemailReportPost,
      startDownload,
      handleDownloadedFile,
      stopDownload,
      removeDownloadFromList,
      removeAllDownloads,
      cancelAllDownloads,
      getInProgressDownloads,
      reDownload,
      initialLoad,
      setMediatId,
      setVideoCallThreadId,
      setCallThreadId,
      setProxyPhoneNumber,
      fetchUnseenVoicemailCount,
      setCallingSearchTerm,
      reset,
      ...toRefs(state),
      ignoreFilterAndSearchUpdates,
      resetMainCallingFeature,
      setCoparentDecliningCall,
      getTestAccessToken,
      setIntroVideoDiagnostics,
      setShowVideoCallError,
      tryShowVideoCall,
      showMobileHeaderCog,
      setCanCallStatus,
      resetCanCallStatus,
      setPhoneSessionError
    }
  },
  { persist: { pick: paths } }
)
